/**
 * SmartChoice Pure Utility Functions
 *
 * Pure business logic functions with zero side effects
 * Can be tested without mocking any dependencies
 */

import { CareCategories, ProviderTypes } from '../../common/Constants';
import {
  COVERAGE_TYPE_RULES,
  FEATURE_FLAG_CATEGORY_MAP,
  NON_SMART_CHOICE_CATEGORIES,
  PROVIDER_TYPE_RULES,
  SmartChoiceContext,
  SmartChoiceEligibilityResult,
  SmartChoiceFlags,
} from './smartChoice.config';

// ============================================================================
// CATEGORY UTILITIES
// ============================================================================

/**
 * Determines which categories are eligible based on feature flags
 *
 * Completely generic - automatically handles ANY flag defined in FEATURE_FLAG_CATEGORY_MAP.
 * No manual changes needed when adding new flags!
 */
export function getEligibleCategories(
  flags: SmartChoiceFlags
): CareCategories[] {
  const eligibleCategories: CareCategories[] = [];

  // Dynamically iterate over all flags in FEATURE_FLAG_CATEGORY_MAP
  // and add categories for enabled flags
  (
    Object.keys(FEATURE_FLAG_CATEGORY_MAP) as Array<
      keyof typeof FEATURE_FLAG_CATEGORY_MAP
    >
  ).forEach((flagKey) => {
    if (flags[flagKey]) {
      eligibleCategories.push(...FEATURE_FLAG_CATEGORY_MAP[flagKey]);
    }
  });

  // Add "PrimaryCare" variant (from dashboard - no space)
  const primaryCareFromDashboard = CareCategories.PRIMARY_CARE.replace(
    ' ',
    ''
  ) as CareCategories;
  if (eligibleCategories.includes(CareCategories.PRIMARY_CARE)) {
    eligibleCategories.push(primaryCareFromDashboard);
  }

  return eligibleCategories;
}

/**
 * Checks if category is in non-SmartChoice list
 */
export function isNonSmartChoiceCategory(category: string): boolean {
  return NON_SMART_CHOICE_CATEGORIES.includes(category as CareCategories);
}

/**
 * Checks if category is in eligible list
 */
export function isEligibleCategory(
  category: string,
  eligibleCategories: CareCategories[]
): boolean {
  return eligibleCategories.includes(category as CareCategories);
}

// ============================================================================
// VALIDATION UTILITIES
// ============================================================================

/**
 * Validates provider type against category requirements
 *
 * Performs case-insensitive matching to handle different casing from various sources
 * (API responses, store values, URL parameters).
 *
 * This function is completely configuration-driven from PROVIDER_TYPE_RULES.
 * If a category has rules defined, it checks against them.
 * If no rules defined, the category doesn't support SmartChoice.
 *
 * @param chipValue - Care category chip value
 * @param providerType - Provider type to validate (case-insensitive)
 * @param flags - Feature flags
 * @returns true if provider type is valid for the category, or if no provider type specified
 */
export function isValidProviderType(
  chipValue: string,
  providerType: string | undefined,
  flags: SmartChoiceFlags
): boolean {
  // No provider type specified - always valid
  if (!providerType) {
    return true;
  }

  // Normalize to lowercase for case-insensitive comparison
  const normalizedProviderType = providerType.toLowerCase().trim();

  // Normalize chipValue (handle "PrimaryCare" variant from dashboard)
  const normalizedChipValue = chipValue.replace(/\s/g, '');
  const primaryCareNoSpace = CareCategories.PRIMARY_CARE.replace(/\s/g, '');

  let categoryToCheck = chipValue as CareCategories;
  if (normalizedChipValue === primaryCareNoSpace) {
    categoryToCheck = CareCategories.PRIMARY_CARE;
  }

  // Dynamically check if category is enabled by ANY feature flag
  // This automatically handles new flags without code changes!
  const isEnabledByAnyFlag = (
    Object.keys(FEATURE_FLAG_CATEGORY_MAP) as Array<
      keyof typeof FEATURE_FLAG_CATEGORY_MAP
    >
  ).some(
    (flagKey) =>
      flags[flagKey] &&
      FEATURE_FLAG_CATEGORY_MAP[flagKey].includes(categoryToCheck)
  );

  if (!isEnabledByAnyFlag) {
    return false;
  }

  // Get allowed provider types from PROVIDER_TYPE_RULES
  const allowedTypes = PROVIDER_TYPE_RULES[categoryToCheck];

  // If no rules defined, category doesn't support provider type validation
  if (!allowedTypes) {
    return false;
  }

  // Case-insensitive comparison
  return allowedTypes.some(
    (type) => type.toLowerCase() === normalizedProviderType
  );
}

/**
 * Validates coverage type for a specific category
 *
 * SmartChoice coverage rules (defined in COVERAGE_TYPE_RULES):
 * - Primary Care: 'M' (Medicare) only
 * - Medical Specialists: 'M' (Medicare) only
 * - Facilities: 'M' (Medicare) only
 * - Other categories: Not supported (return false)
 *
 * @param coverageType - Coverage type code (e.g., 'M' for Medicare, 'B' for Behavioral)
 * @param category - Care category to check
 * @returns true if coverage type is allowed for this category
 *
 * @example
 * validateCoverageTypeForCategory('M', CareCategories.PRIMARY_CARE) // true
 * validateCoverageTypeForCategory('B', CareCategories.PRIMARY_CARE) // false
 * validateCoverageTypeForCategory('M', CareCategories.FACILITIES) // true
 * validateCoverageTypeForCategory('B', CareCategories.FACILITIES) // false
 * validateCoverageTypeForCategory('V', CareCategories.PRIMARY_CARE) // false (not supported)
 * validateCoverageTypeForCategory('M', CareCategories.DENTAL) // false (no rules defined)
 */
export function validateCoverageTypeForCategory(
  coverageType: string | undefined,
  category: string
): boolean {
  if (!coverageType) {
    return true;
  }

  // Get allowed coverage types for this category
  const allowedCoverageTypes = COVERAGE_TYPE_RULES[category as CareCategories];

  // Category has no coverage rules defined = not allowed
  if (!allowedCoverageTypes) {
    return false;
  }

  // Check if coverage type is in allowed list for this category
  return allowedCoverageTypes.includes(coverageType);
}

/**
 * Checks if section type disqualifies SmartChoice
 */
export function isDisqualifiedSectionType(sectionType: string | undefined): {
  isGroupType: boolean;
} {
  const sectionTypeLower = sectionType?.toLowerCase();

  return {
    isGroupType: sectionTypeLower === ProviderTypes.GROUP?.toLowerCase(),
  };
}

/**
 * Checks if there's a mismatch between category and section type
 *
 * For example:
 * - chipValue=PRIMARY_CARE + sectionType=facility → MISMATCH (PRIMARY_CARE only supports PRACTITIONER)
 * - chipValue=FACILITIES + sectionType=facility → OK (FACILITIES supports ORGANIZATION)
 * - chipValue=FACILITIES + sectionType=Provider → OK (FACILITIES supports PRACTITIONER)
 *
 * @param chipValue - Care category
 * @param sectionType - Section type from URL
 * @returns true if there's a mismatch that should disqualify SmartChoice
 */
export function hasCategorySectionTypeMismatch(
  chipValue: string,
  sectionType: string | undefined
): boolean {
  if (!sectionType) {
    return false;
  }

  const sectionTypeLower = sectionType.toLowerCase().trim();

  // If sectionType is "facility" or "organization", check if category allows ORGANIZATION provider type
  if (sectionTypeLower === 'facility' || sectionTypeLower === 'organization') {
    const allowedTypes = PROVIDER_TYPE_RULES[chipValue as CareCategories];

    if (!allowedTypes) {
      return true; // No rules = not supported
    }

    // Check if ORGANIZATION is in allowed types
    return !allowedTypes.some(
      (type) => type.toLowerCase() === ProviderTypes.ORGANIZATION.toLowerCase()
    );
  }

  return false;
}

// ============================================================================
// CORE ELIGIBILITY ALGORITHM
// ============================================================================

/**
 * **SINGLE SOURCE OF TRUTH**
 *
 * Core SmartChoice eligibility determination logic
 * Maintains same behavior as original implementation
 *
 * @param context - Complete SmartChoice context
 * @param flags - Feature flags
 * @param ffAndSupressionFFCheckOnly - When true, only check flags (for "All" chip)
 * @returns Eligibility result
 */
export function determineSmartChoiceEligibility(
  context: SmartChoiceContext,
  flags: SmartChoiceFlags,
  ffAndSupressionFFCheckOnly: boolean
): SmartChoiceEligibilityResult {
  const {
    chipValue,
    sectionType,
    searchType,
    searchBySpeciality,
    providerType,
  } = context;

  // Build eligible categories
  const eligibleCategories = getEligibleCategories(flags);
  const isSmartChoiceEligibleCategory = eligibleCategories.includes(
    chipValue as CareCategories
  );

  // Check search type restrictions
  const isNameSearch = searchType === 'name';
  const isNotSpecialitySearch = searchBySpeciality === 'false';

  // Check section type
  const { isGroupType } = isDisqualifiedSectionType(sectionType);

  // Check for category/section type mismatch
  const hasSectionTypeMismatch = hasCategorySectionTypeMismatch(
    chipValue,
    sectionType
  );

  // Base condition: flags check
  const baseCondition =
    !flags.isSmartChoiceSuppressed &&
    (flags.enablePSXProviderRecommendations ||
      flags.enablePSXFacilitiesRecommendations);

  // If only checking flags (for "All" chip scenario)
  if (ffAndSupressionFFCheckOnly) {
    return {
      isEnabled: baseCondition,
      isAvailable: baseCondition,
    };
  }

  // Early returns for disqualifying conditions
  if (isNotSpecialitySearch || isNameSearch) {
    return { isEnabled: false, isAvailable: baseCondition };
  }

  // Early return if both flags are OFF
  if (
    !flags.enablePSXProviderRecommendations &&
    !flags.enablePSXFacilitiesRecommendations
  ) {
    return { isEnabled: false, isAvailable: false };
  }

  // Base conditions check - including section type mismatch
  if (
    !baseCondition ||
    !isSmartChoiceEligibleCategory ||
    isGroupType ||
    hasSectionTypeMismatch
  ) {
    return { isEnabled: false, isAvailable: baseCondition };
  }

  // Provider type validation
  const validProviderType = isValidProviderType(chipValue, providerType, flags);
  if (!validProviderType) {
    return { isEnabled: false, isAvailable: baseCondition };
  }

  // Coverage type validation (category-specific)
  // Only Primary Care, Medical Specialists, and Facilities support coverage 'M'
  const validCoverageType = validateCoverageTypeForCategory(
    context.coverageType,
    chipValue
  );
  if (!validCoverageType) {
    return { isEnabled: false, isAvailable: baseCondition };
  }

  return {
    isEnabled: true,
    isAvailable: baseCondition,
  };
}
